/**
 * Native renderer for app widgets on the Space grid (ADR-0015). Apps return
 * JSON; the shell draws it in the Omni look at the chosen size, following
 * the "Grid & widget sizes" spec: 2x1, 2x2, 2x3, 4x1 (action), 4x2, 4x3.
 * Kinds: `metric` (a figure and its breakdown), `action` (a shortcut), `event`
 * (the next thing in a calendar) and `map` (a place on a small map).
 */
import { useEffect, useState, type CSSProperties } from "react";

import { resolveUrl } from "../lib/api";
import { appGet } from "../lib/appSessions";
import { onEvent } from "../lib/events";
import { load, save } from "../lib/storage";
import { amountShort, money } from "../lib/format";
import { seriesColor } from "../lib/series";
import { useTheme } from "../lib/theme";
import type { IconSource } from "./AppIcon";
import { Icon } from "./Icon";

export interface Money {
  label: string;
  short_label?: string;
  amount?: number;
  currency?: string;
  text?: string;
}

export interface WidgetData {
  kind: "metric" | "action" | "event" | "map";
  caption?: string;
  value?: Money;
  secondary?: Money;
  segments?: { label: string; amount: number; other?: boolean }[];
  rows?: { label: string; amount?: number; currency?: string; text?: string; signed?: boolean }[];
  // action
  icon?: string;
  title?: string;
  subtitle?: string;
  button?: string;
  path?: string;
  /** "accent" for a shortcut (jade), default for something waiting (amber) */
  tone?: "accent" | "attention";
  /** event and map: shown when there's nothing (no event coming, no position yet) */
  empty?: string;
  // event: ISO times; `later` = how many more that day
  start?: string;
  end?: string;
  where?: string;
  with?: string | null;
  later?: number;
  // map: a point, drawn on the app's tiles
  lat?: number;
  lon?: number;
  accuracy?: number | null;
  at?: string;
  place?: string | null;
  zoom?: number;
  tiles?: string;
  dark_tiles?: string | null;
  attribution?: string;
}

export type Size = "2x1" | "2x2" | "2x3" | "4x1" | "4x2" | "4x3";

function fmt(m: Money | undefined, short = false): string {
  if (!m) return "";
  if (m.amount == null) return m.text ?? "";
  return short ? money(m.amount, m.currency ?? "PLN", { decimals: false }) : money(m.amount, m.currency ?? "PLN");
}

/**
 * A widget's data: the last copy shows at once (ADR-0024), then it's refreshed,
 * and refreshed again whenever its app publishes an event (ADR-0025).
 */
export function useWidgetData(appId: string, widgetId: string, size: string, enabled = true) {
  const key = `omni.widget:${appId}:${widgetId}:${size}`;
  const [data, setData] = useState<WidgetData | null>(() => load<WidgetData | null>(key, null));
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setData(load<WidgetData | null>(key, null));
    const fetchNow = () =>
      appGet<WidgetData>(appId, `/widgets/${widgetId}`, { size })
        .then((d) => {
          if (!alive) return;
          setData(d);
          setError(false);
          save(key, d);
        })
        .catch(() => alive && setError(true));
    fetchNow();
    // Time moves on: a calendar widget refreshes when its event ends, a map every few minutes
    let ticks = 0;
    const every = setInterval(() => {
      const d = load<WidgetData | null>(key, null);
      ticks += 1;
      if ((d?.kind === "map" && ticks % 5 === 0) || (d?.kind === "event" && d.end && Date.parse(d.end) <= Date.now())) fetchNow();
    }, 60_000);
    const off = onEvent((e) => {
      if (e.app_id !== appId) return;
      clearTimeout(timer);
      timer = setTimeout(fetchNow, 600); // a burst of events refreshes once
    });
    return () => {
      alive = false;
      clearTimeout(timer);
      clearInterval(every);
      off();
    };
  }, [appId, widgetId, size, enabled, key]);
  return { data, error: error && !data };
}

function TileIcon({ app }: { app: IconSource }) {
  return (
    <span className="tile-ic" style={{ "--tile-bg": app.icon_background ?? undefined } as CSSProperties}>
      {app.icon_url ? <img src={resolveUrl(app.icon_url)} alt="" /> : <span className="xs">{app.name[0]}</span>}
    </span>
  );
}

function Breakdown({ segments, thick }: { segments: NonNullable<WidgetData["segments"]>; thick?: boolean }) {
  const { theme } = useTheme();
  const total = segments.reduce((s, x) => s + x.amount, 0);
  if (!total) return null;
  return (
    <div className={`bar${thick ? " thick" : ""}`} role="img" aria-label={segments.map((s) => `${s.label} ${amountShort(s.amount)}`).join(", ")}>
      {segments.map((s, i) => (
        <span key={s.label} style={{ flexGrow: s.amount, background: seriesColor(theme.mode, i, s.other) }} />
      ))}
    </div>
  );
}

export function Legend({ segments, columns = 1, max = 4 }: { segments: NonNullable<WidgetData["segments"]>; columns?: 1 | 2; max?: number }) {
  const { theme } = useTheme();
  return (
    <div className={`legend${columns === 2 ? " two" : ""}`}>
      {segments.slice(0, max).map((s, i) => (
        <span key={s.label} className="legend-item">
          <span className="swatch" style={{ background: seriesColor(theme.mode, i, s.other) }} />
          <span className="grow">{s.label}</span>
          <span className="num">{amountShort(s.amount)}</span>
        </span>
      ))}
    </div>
  );
}

function Skeleton({ size }: { size: Size }) {
  const rows = size.endsWith("1") ? 1 : 3;
  return (
    <div className="stack-sm" style={{ flex: 1, justifyContent: "flex-end" }} aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="tile-skeleton" style={{ height: i === 0 && rows > 1 ? 22 : 10, width: i === 0 ? "70%" : "100%" }} />
      ))}
    </div>
  );
}

/** "Today", "Tomorrow", or the weekday and date. */
function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(d) - day(now)) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return d.toLocaleDateString(undefined, diff > 0 && diff < 7 ? { weekday: "long" } : { weekday: "short", day: "numeric", month: "short" });
}
const clock = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

/** A big time with a small AM/PM, in the person's own clock format. */
function BigTime({ iso }: { iso: string }) {
  const parts = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).formatToParts(new Date(iso));
  return (
    <span className="ev-time">
      {parts.map((p, i) => (p.type === "dayPeriod" ? <small key={i}>{p.value}</small> : <span key={i}>{p.value}</span>))}
    </span>
  );
}

/** The next thing in a calendar: the time, the day and what it is (2x2), or a line of each (2x1). */
function EventBody({ app, size, data }: { app: IconSource; size: Size; data: WidgetData }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 60_000); return () => clearInterval(t); }, []);
  const now = Date.now();
  const on = !!data.start && Date.parse(data.start) <= now && (!data.end || Date.parse(data.end) > now);
  const soon = !!data.start && !on && Date.parse(data.start) - now < 30 * 60_000;
  const when = data.start ? (on ? `Now · until ${clock(data.end ?? data.start)}` : `${dayLabel(data.start)}${size === "2x1" ? ` ${clock(data.start)}` : ""}`) : "";
  const sub = [data.with ? `With ${data.with}` : "", data.where ?? ""].filter(Boolean)[0] ?? "";

  if (size === "2x1") {
    return (
      <>
        <TileIcon app={app} />
        <span className="stack" style={{ gap: 0, minWidth: 0 }}>
          <span className={`xs${on || soon ? " ev-now" : " muted"}`} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{data.empty ? app.name : when}</span>
          <span className="ev-title one">{data.empty ?? data.title}</span>
        </span>
      </>
    );
  }
  return (
    <>
      <div className="tile-head"><TileIcon app={app} /><span className="grow">{app.name}</span></div>
      {data.empty ? (
        <span className="stack" style={{ gap: 2, marginTop: "auto" }}>
          <span className="ev-title">{data.empty}</span>
          <span className="xs muted">Tap to open your calendar</span>
        </span>
      ) : (
        <span className="stack" style={{ gap: 2, marginTop: "auto", minWidth: 0 }}>
          <span className={`xs${on || soon ? " ev-now" : " muted"}`}>{when}</span>
          {!on && data.start && <BigTime iso={data.start} />}
          <span className="ev-title">{data.title}</span>
          {(sub || !!data.later) && <span className="xs muted" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub || `${data.later} more that day`}</span>}
        </span>
      )}
    </>
  );
}

const TILE = 128; // CSS px per map tile, drawn one zoom level deeper so it's sharp on phones

function tileUrl(template: string, z: number, x: number, y: number): string {
  return template.replace("{z}", String(z)).replace("{x}", String(x)).replace("{y}", String(y)).replace("{s}", "a").replace("{r}", "");
}

/** A point on a small map made of the app's tiles (no map library, no app code). */
function MapBody({ app, data }: { app: IconSource; data: WidgetData }) {
  const { theme } = useTheme();
  const dark = theme.mode === "dark";
  if (data.lat == null || data.lon == null || !data.tiles) {
    return (
      <>
        <div className="tile-head"><TileIcon app={app} /><span className="grow">{app.name}</span></div>
        <span className="stack" style={{ gap: 2, marginTop: "auto" }}>
          <span className="ev-title">{data.empty ?? "No location yet"}</span>
          <span className="xs muted">Tap to share where you are</span>
        </span>
      </>
    );
  }
  const z = Math.min(Math.max(Math.round(data.zoom ?? 15), 2), 18) + 1;
  const n = 2 ** z;
  const lat = Math.max(Math.min(data.lat, 85), -85) * Math.PI / 180;
  const fx = ((data.lon + 180) / 360) * n;
  const fy = ((1 - Math.log(Math.tan(lat) + 1 / Math.cos(lat)) / Math.PI) / 2) * n;
  const cx = Math.floor(fx), cy = Math.floor(fy);
  const template = dark && data.dark_tiles ? data.dark_tiles : data.tiles;
  const tiles: { x: number; y: number; left: number; top: number }[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const ty = cy + dy;
      if (ty < 0 || ty >= n) continue;
      tiles.push({ x: (((cx + dx) % n) + n) % n, y: ty, left: (cx + dx - fx) * TILE, top: (ty - fy) * TILE });
    }
  }
  const metersPerPx = (156543.03392 * Math.cos(lat)) / 2 ** z * (256 / TILE);
  const r = data.accuracy ? data.accuracy / metersPerPx : 0;
  const ago = data.at ? agoText(data.at) : "";
  return (
    <>
      <div className={`map-tiles${dark && !data.dark_tiles ? " inverted" : ""}`} aria-hidden>
        {tiles.map((t) => (
          <img key={`${t.x}/${t.y}`} src={tileUrl(template, z, t.x, t.y)} alt="" draggable={false}
            style={{ left: `calc(50% + ${t.left}px)`, top: `calc(50% + ${t.top}px)`, width: TILE, height: TILE }} />
        ))}
      </div>
      {r >= 8 && r <= 90 && <span className="map-acc" style={{ width: r * 2, height: r * 2 }} aria-hidden />}
      <span className="map-pin" aria-hidden />
      <span className="map-label">
        <span className="ev-title one">{data.place ?? "You're here"}</span>
        {ago && <span className="xs muted">{ago}</span>}
      </span>
      {data.attribution && <span className="map-attr">{data.attribution}</span>}
    </>
  );
}

function agoText(iso: string): string {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** The widget body at a size. `data` null = loading. */
export function WidgetBody({ app, size, data, error }: { app: IconSource; size: Size; data: WidgetData | null; error?: boolean }) {
  const [cols, rows] = size.split("x").map(Number);

  if (data?.kind === "event") return <EventBody app={app} size={size} data={data} />;
  if (data?.kind === "map") return <MapBody app={app} data={data} />;
  if (data?.kind === "action" || (size === "4x1" && !data)) {
    return (
      <>
        <span className={`action-ic${data?.tone === "accent" ? " accent" : ""}`}>
          <Icon name={data?.icon ?? "apps"} />
        </span>
        <span className="grow stack" style={{ gap: 1 }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{data?.title ?? " "}</span>
          <span className="xs muted">{data?.subtitle ?? app.name}</span>
        </span>
        {data?.button && cols === 4 && <span className="action-btn">{data.button}</span>}
      </>
    );
  }

  const head = (
    <div className="tile-head">
      <TileIcon app={app} />
      <span className="grow">{app.name}</span>
      {cols === 4 && data?.caption && <span className="caption">{data.caption}</span>}
    </div>
  );
  if (error) return <>{head}<p className="small muted" style={{ marginTop: "auto" }}>Couldn't load</p></>;
  if (!data) return <>{head}<Skeleton size={size} /></>;
  const segs = data.segments ?? [];

  if (rows === 1) {
    // 2x1: icon + one figure
    return (
      <>
        <TileIcon app={app} />
        <span className="stack" style={{ gap: 0, minWidth: 0 }}>
          <span className="xs muted">{data.value?.short_label ?? data.value?.label}</span>
          <span className="num" style={{ fontSize: 16 }}>{fmt(data.value, true)}</span>
        </span>
      </>
    );
  }

  if (size === "4x3") {
    return (
      <>
        {head}
        {data.secondary && (
          <div className="tile-kv">
            <span className="xs muted">{data.secondary.label}</span>
            <span className="num lg">{fmt(data.secondary)}</span>
          </div>
        )}
        <div className="tile-kv">
          <span className="xs muted">{data.value?.label}</span>
          <span className={`num${data.secondary ? "" : " lg"}`} style={data.secondary ? { fontSize: 15 } : undefined}>{fmt(data.value)}</span>
        </div>
        <Breakdown segments={segs} />
        {data.rows && data.rows.length > 0 ? (
          <div className="tile-rows">
            {data.rows.slice(0, 2).map((r, i) => (
              <div key={i}>
                <span className="grow" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</span>
                <span className={`num${r.amount != null && r.amount > 0 ? " pos" : ""}`}>
                  {r.amount != null ? money(r.amount, r.currency ?? "PLN", { sign: r.signed }) : r.text}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <Legend segments={segs} columns={2} />
        )}
      </>
    );
  }

  return (
    <>
      {head}
      {cols === 4 ? (
        <span className="num" style={{ fontSize: 22, letterSpacing: "-0.03em", marginTop: "auto" }}>{fmt(data.value)}</span>
      ) : (
        <div className="tile-value">
          <span className="xs muted">{data.value?.label}</span>
          <span className="num">{fmt(data.value)}</span>
        </div>
      )}
      <Breakdown segments={segs} />
      {(rows >= 3 || cols === 4) && segs.length > 0 && <Legend segments={segs} columns={cols === 4 ? 2 : 1} />}
      {segs.length === 0 && rows >= 2 && <span className="xs muted">Nothing to break down yet</span>}
    </>
  );
}

export function WidgetTile({ app, appId, widgetId, size, onOpen, tabIndex }: { app: IconSource; appId: string; widgetId: string; size: Size; onOpen: (path?: string) => void; tabIndex?: number }) {
  const { data, error } = useWidgetData(appId, widgetId, size);
  const action = data?.kind === "action" || size === "4x1";
  const cls = `tile span-${size}${action ? " action" : size.endsWith("x1") ? " row1" : ""}${data?.kind === "map" ? " map" : ""}`;
  return (
    <button className={cls} tabIndex={tabIndex} onClick={() => onOpen(data?.path)} aria-label={`${app.name}: ${data?.title ?? data?.place ?? data?.empty ?? data?.value?.label ?? "widget"}`}>
      <WidgetBody app={app} size={size} data={data} error={error} />
    </button>
  );
}
